import { useEffect, useState } from "react";
import { getMyPrivacy, setResearchConsent, setDemographics } from "../api.js";
import { AGE_BANDS, JOBS, GENDERS, hasAny } from "../demographics.js";
import Choice from "../ui/Choice.jsx";
import Icon from "../ui/Icon.jsx";
import Booting from "./Booting.jsx";
import Broken from "./Broken.jsx";

const EMPTY = { ageBand: null, job: null, gender: null };

/**
 * 개인정보 설정.
 *
 * 방침에 「언제든 그만둘 수 있습니다」 라고 적어 뒀다. 이 화면이 그 문장이다.
 * 철회할 방법 없이 받은 선택 동의는 동의로 인정되지 않을 수 있고,
 * 그러면 그 동의로 모은 데이터가 통째로 못 쓰게 된다.
 */
export default function Privacy({ onBack }) {
  const [state, setState] = useState("loading"); // loading | ready | failed
  const [err, setErr] = useState(null);
  const [consent, setConsent] = useState(false);
  const [who, setWho] = useState(EMPTY);
  const [saving, setSaving] = useState(null); // null | "consent" | "who"
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const me = await getMyPrivacy();
        if (!alive) return;
        setConsent(me.researchConsent);
        setWho({ ...EMPTY, ...(me.demographics ?? {}) });
        setState("ready");
      } catch (e) {
        if (alive) { setErr(e); setState("failed"); }
      }
    })();
    return () => { alive = false; };
  }, []);

  async function toggle(next) {
    setSaving("consent");
    setSaved(null);
    try {
      await setResearchConsent(next);
      setConsent(next);
      // 서버가 철회와 함께 인구통계를 지운다. 화면도 같이 비워야 거짓말이 안 된다.
      if (!next) setWho(EMPTY);
      setSaved(next ? "동의했어요." : "그만뒀어요. 넣어 두셨던 연령대·직군·성별도 지웠어요.");
    } catch (e) {
      setErr(e);
      setState("failed");
    } finally {
      setSaving(null);
    }
  }

  async function saveWho(next) {
    setWho(next);
    setSaving("who");
    setSaved(null);
    try {
      await setDemographics(hasAny(next) ? next : null);
      setSaved(hasAny(next) ? "저장했어요." : "지웠어요.");
    } catch (e) {
      setErr(e);
      setState("failed");
    } finally {
      setSaving(null);
    }
  }

  if (state === "failed") return <Broken err={err} onRetry={() => window.location.reload()} />;
  if (state === "loading") return <Booting />;

  const set = (k) => (v) => saveWho({ ...who, [k]: v });

  return (
    <main className="screen" style={{ minHeight: "100vh" }}>
      <header className="bar">
        <button type="button" className="icon-btn" aria-label="뒤로" onClick={onBack}>
          <Icon name="back" />
        </button>
        <span className="grow" />
      </header>

      <h1 className="title">개인정보 설정</h1>

      <section className="card raised" style={{ borderRadius: 4, padding: "20px 20px", gap: 12 }}>
        <h2 style={{ fontSize: 17, fontWeight: 800 }}>
          연구·통계에 쓰는 것 <span style={{ color: "var(--muted)" }}>(선택)</span>
        </h2>
        <p className="note" style={{ margin: 0 }}>
          개인을 알아볼 수 없는 통계를 만드는 데 씁니다. 제 발언 원문이 그대로 팔리지는 않아요.{" "}
          <a href="https://sowhat.run/privacy" target="_blank" rel="noreferrer" style={{ textDecoration: "underline" }}>
            자세히
          </a>
        </p>
        <button
          type="button"
          className={consent ? "btn" : "btn primary"}
          style={{ alignSelf: "flex-start", minHeight: 44 }}
          disabled={saving === "consent"}
          onClick={() => toggle(!consent)}
        >
          {saving === "consent" ? "바꾸는 중…" : consent ? "그만두기" : "동의하기"}
        </button>
        <p className="note" style={{ margin: 0 }}>
          {consent ? "지금 동의한 상태예요." : "지금은 쓰지 않아요. 동의하지 않아도 서비스는 그대로예요."}
        </p>
      </section>

      {/* 동의하지 않은 사람에게는 묻지 않는다. 안 쓸 값을 받아 두는 게 곧 과잉 수집이다. */}
      {consent && (
        <section className="card" style={{ borderRadius: 4, padding: "20px 20px", gap: 14 }}>
          <h2 style={{ fontSize: 17, fontWeight: 800 }}>
            통계에 쓸 정보 <span style={{ color: "var(--muted)" }}>(선택)</span>
          </h2>
          <p className="note" style={{ margin: 0 }}>
            생년월일이나 직장이 아니라 구간만 받아요. 안 고르셔도 괜찮고,
            누른 걸 다시 누르면 지워져요.
          </p>
          <Choice label="연령대" options={AGE_BANDS} value={who.ageBand} onChange={set("ageBand")} />
          <Choice label="직군" options={JOBS} value={who.job} onChange={set("job")} />
          <Choice label="성별" options={GENDERS} value={who.gender} onChange={set("gender")} />
          {hasAny(who) && (
            <button type="button" className="btn" style={{ alignSelf: "flex-start", minHeight: 44 }}
              disabled={saving === "who"} onClick={() => saveWho(EMPTY)}>
              전부 지우기
            </button>
          )}
        </section>
      )}

      <p aria-live="polite" className="note" style={{ minHeight: 20 }}>
        {saving === "who" ? "저장하는 중…" : saved ?? ""}
      </p>
    </main>
  );
}
